import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';
import { 
  Input, 
  Textarea, 
  Select, 
  Checkbox, 
  FormButtons 
} from '../ui/FormComponents';
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const AddCustomer = ({ isOpen, onClose, onCustomerAdded }) => {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'b2c',
    status: 'active',
    // B2B specific fields
    company_id: '',
    // Address
    street: '',
    city: '',
    postal_code: '',
    country: 'Israel',
    // Additional
    notes: '',
    newsletter_subscribed: false,
    marketing_consent: false
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies?limit=100');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    setErrors(prev => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name.trim()) newErrors.first_name = 'שם פרטי נדרש';
    if (!formData.last_name.trim()) newErrors.last_name = 'שם משפחה נדרש';
    if (!formData.email.trim()) newErrors.email = 'אימייל נדרש';
    if (!formData.phone.trim()) newErrors.phone = 'מספר טלפון נדרש';
    
    // Email validation
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'כתובת אימייל לא תקינה';
    }

    // Phone validation (basic Israeli phone format)
    if (formData.phone && !/^[+]?[0-9\-\s()]{8,15}$/.test(formData.phone)) {
      newErrors.phone = 'מספר טלפון לא תקין';
    }

    // B2B specific validations
    if (formData.role === 'b2b') {
      if (!formData.company_id) newErrors.company_id = 'חברה נדרשת עבור לקוח B2B';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        // Generate a temporary password (user will need to reset)
        password: 'TempPass123!',
        // Generate full name
        full_name: `${formData.first_name} ${formData.last_name}`.trim()
      };

      // Remove company_id for B2C users (don't send empty string)
      if (formData.role === 'b2c') {
        delete submitData.company_id;
      }

      // Submit to API
      const response = await axios.post('/api/users', submitData);

      if (response.data.user) {
        // Success notification
        alert('הלקוח נוסף בהצלחה!');
        
        // Reset form
        resetForm();
        
        // Notify parent component
        if (onCustomerAdded) {
          onCustomerAdded(response.data.user);
        }
        
        // Close modal
        onClose();
      }

    } catch (error) {
      console.error('Error creating customer:', error);
      
      // Show more specific error message
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg).join(', ');
        alert(`שגיאה ביצירת הלקוח: ${errorMessages}`);
      } else {
        alert('שגיאה ביצירת הלקוח. אנא נסה שנית.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      role: 'b2c',
      status: 'active',
      company_id: '',
      street: '',
      city: '',
      postal_code: '',
      country: 'Israel',
      notes: '',
      newsletter_subscribed: false,
      marketing_consent: false
    });
    setErrors({});
  };

  const handleClose = useCallback(() => {
    if (!loading) {
      resetForm();
      onClose();
    }
  }, [loading, onClose]);

  // Memoize company options to prevent re-renders
  const companyOptions = useMemo(() => [
    { value: '', label: 'בחר חברה...' },
    ...companies.map(company => ({
      value: company.id,
      label: `${company.name} (${company.business_number || 'ללא מספר עסק'})`
    }))
  ], [companies]);

  // Memoize country options
  const countryOptions = useMemo(() => [
    { value: 'Israel', label: 'ישראל' },
    { value: 'Palestine', label: 'פלסטין' },
    { value: 'Jordan', label: 'ירדן' },
    { value: 'Lebanon', label: 'לבנון' }
  ], []);

  // Memoize status options
  const statusOptions = useMemo(() => [
    { value: 'active', label: 'פעיל' },
    { value: 'inactive', label: 'לא פעיל' }
  ], []);

  // Check if form is valid for submit button
  const isFormValid = useMemo(() => {
    return formData.first_name.trim() && 
           formData.last_name.trim() && 
           formData.email.trim() && 
           formData.phone.trim() &&
           (formData.role !== 'b2b' || formData.company_id) &&
           Object.keys(errors).length === 0;
  }, [formData, errors]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="הוסף לקוח חדש"
      size="lg"
      preventClose={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Customer Role */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-3">תפקיד לקוח</label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="role"
                value="b2c"
                checked={formData.role === 'b2c'}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <UserIcon className="h-5 w-5 text-green-500 mr-2 ml-2" />
              <span className="text-sm text-gray-700">לקוח פרטי (B2C)</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="role"
                value="b2b"
                checked={formData.role === 'b2b'}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
              />
              <BuildingOfficeIcon className="h-5 w-5 text-purple-500 mr-2 ml-2" />
              <span className="text-sm text-gray-700">לקוח עסקי (B2B)</span>
            </label>
          </div>
        </div>

        {/* B2B Company Selection */}
        {formData.role === 'b2b' && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 ml-2" />
              פרטי חברה
            </h4>
            
            <div className="mt-4">
              <Select
                label="בחר חברה *"
                name="company_id"
                value={formData.company_id}
                onChange={handleInputChange}
                error={errors.company_id}
                options={companyOptions}
                help="בחר את החברה שאליה שייך הלקוח"
              />
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <UserIcon className="h-5 w-5 ml-2" />
            פרטים אישיים
          </h4>
          
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="שם פרטי"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              error={errors.first_name}
              placeholder="הזן שם פרטי..."
            />

            <Input
              label="שם משפחה"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              error={errors.last_name}
              placeholder="הזן שם משפחה..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="כתובת אימייל"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              error={errors.email}
              placeholder="example@email.com"
              icon={EnvelopeIcon}
            />

            <Input
              label="מספר טלפון"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              required
              error={errors.phone}
              placeholder="+972-50-1234567"
              icon={PhoneIcon}
            />
          </div>
        </div>

        {/* B2B Specific Fields */}
        {formData.type === 'b2b' && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 ml-2" />
              פרטי חברה
            </h4>
            
            <Input
              label="שם החברה"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              required={formData.type === 'b2b'}
              error={errors.company_name}
              placeholder="הזן שם חברה..."
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="תפקיד"
                name="job_title"
                value={formData.job_title}
                onChange={handleInputChange}
                placeholder="מנהל רכישות, מנכ״ל..."
              />

              <Input
                label="מספר עוסק/חברה"
                name="tax_id"
                value={formData.tax_id}
                onChange={handleInputChange}
                placeholder="123456789"
              />
            </div>
          </div>
        )}

        {/* Address Information */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center">
            <MapPinIcon className="h-5 w-5 ml-2" />
            כתובת
          </h4>
          
          <Input
            label="רחוב"
            name="street"
            value={formData.street}
            onChange={handleInputChange}
            placeholder="רחוב הרצל 123"
          />

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="עיר"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="תל אביב"
            />

            <Input
              label="מיקוד"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              placeholder="61231"
            />

            <Select
              label="מדינה"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              options={countryOptions}
            />
          </div>
        </div>

        {/* Additional Settings */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 border-b pb-2">
            הגדרות נוספות
          </h4>
          
          <Select
            label="סטטוס לקוח"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            options={statusOptions}
          />

          <Textarea
            label="הערות"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            placeholder="הערות נוספות על הלקוח..."
          />

          <div className="space-y-3">
            <Checkbox
              label="הרשמה לניוזלטר"
              name="newsletter_subscribed"
              checked={formData.newsletter_subscribed}
              onChange={handleInputChange}
              help="הלקוח יקבל עדכונים על מוצרים חדשים והצעות מיוחדות"
            />

            <Checkbox
              label="הסכמה לשיווק"
              name="marketing_consent"
              checked={formData.marketing_consent}
              onChange={handleInputChange}
              help="הסכמה לקבלת חומרי פרסום ושיווק"
            />
          </div>
        </div>

        {/* Form Actions */}
        <FormButtons
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitText="הוסף לקוח"
          loading={loading}
          submitDisabled={!isFormValid}
        />
      </form>
    </Modal>
  );
};

export default AddCustomer;
